import type { TFunction } from 'i18next';
import type { ProbeType, ShipKind, CalibrationProvider, TreatmentAction } from './types';

// #991 Upcoming tab — code → i18n label helpers, shared by the matrix, the KPI
// tiles, the weekly chart and the per-probe table so labels stay in one place.

// #1118 pt4 — O2 carries its own code: it is calibrated in the ATEK lab (ships
// both ways) while CO2 gas is calibrated on-site, so the two never share a badge.
// #1118 pt5 — OVEN likewise: the -80 étalon (ETAL) and the oven/incubator
// reference are distinct jobs and no longer share the "Reference / oven" label.
export const PROBE_TYPES: ProbeType[] = ['ETAL', 'OVEN', 'CRYO', 'NUM', 'TH', 'GAS', 'O2'];

const PROBE_TYPE_FALLBACK: Record<ProbeType, string> = {
  ETAL: 'Reference (−80)',
  OVEN: 'Reference / oven',
  CRYO: 'Cryo (−150)',
  NUM: 'Digital temperature',
  TH: 'Temperature / Humidity',
  GAS: 'Gas (CO₂)',
  O2: 'Gas (O₂)',
};

export function probeTypeLabel(t: TFunction, code: ProbeType | null): string {
  if (!code) return t('calibrationPlanning.probeType.unclassified', 'Unclassified');
  return t(`calibrationPlanning.probeType.${code}`, PROBE_TYPE_FALLBACK[code]);
}

export function serviceModeLabel(t: TFunction, mode: 'onsite' | 'mail' | null): string {
  if (mode === 'onsite') return t('calibrationPlanning.serviceMode.onsite', 'On-site visit');
  if (mode === 'mail') return t('calibrationPlanning.serviceMode.mail', 'Shipped');
  return t('calibrationPlanning.serviceMode.review', 'To review');
}

// 'probe-to-lab' names the journey BACKWARDS for the user: ATEK ships a pre-calibrated
// REPLACEMENT probe out to the client (lab -> client), it does not pull the client's probe
// in. The code name is kept (it feeds the lab-capacity counters) but the rendered string is
// corrected — confirmed by Hugo 2026-09-30.
export function shipKindLabel(t: TFunction, kind: ShipKind | null): string {
  if (kind === 'probe-to-lab') return t('calibrationPlanning.shipKind.probeToLab', 'Replacement probe shipped');
  if (kind === 'etalon-to-client') return t('calibrationPlanning.shipKind.etalonToClient', 'Calibrator shipped');
  return t('calibrationPlanning.shipKind.none', '—');
}

// #1118 — the four treatments Hugo enumerated: mode x probe family.
export const TREATMENT_ACTIONS: TreatmentAction[] = [
  'ship-replacement-probe', 'ship-calibrator', 'onsite-swap', 'onsite-calibrate',
];

const ACTION_FALLBACK: Record<TreatmentAction, string> = {
  'ship-replacement-probe': 'Replacement probe shipped',
  'ship-calibrator': 'Calibrator shipped',
  'onsite-swap': 'Swap on site',
  'onsite-calibrate': 'Calibrate on site',
};

export function actionLabel(t: TFunction, action: TreatmentAction | null): string {
  if (!action) return t('calibrationPlanning.action.none', '—');
  return t(`calibrationPlanning.action.${action}`, ACTION_FALLBACK[action]);
}

export const ACTION_VARIANT: Record<TreatmentAction, 'info' | 'warning' | 'success' | 'purple'> = {
  'ship-replacement-probe': 'info',
  'ship-calibrator': 'warning',
  'onsite-swap': 'success',
  'onsite-calibrate': 'purple',
};

export function providerLabel(t: TFunction, provider: CalibrationProvider): string {
  if (provider === 'atek') return t('calibrationPlanning.provider.atek', 'ATEK');
  if (provider === 'client') return t('calibrationPlanning.provider.client', 'Client');
  return t('calibrationPlanning.provider.other', 'To verify');
}

// Badge colour variants (kept in sync with the @/components/ui/badge variants).
export const PROVIDER_VARIANT: Record<CalibrationProvider, 'success' | 'info' | 'warning'> = {
  atek: 'success',
  client: 'info',
  other: 'warning',
};

export const PROBE_TYPE_VARIANT: Record<ProbeType, 'purple' | 'info' | 'secondary' | 'success' | 'danger' | 'warning'> = {
  ETAL: 'purple',
  OVEN: 'info',
  CRYO: 'info',
  NUM: 'secondary',
  TH: 'success',
  GAS: 'danger',
  O2: 'warning',
};
